/**
 * A customer's rate access codes, grouped by GDS.
 *
 * WHY THIS EXISTS. Stacie has asked for it in three consecutive sessions
 * (2026-08-26, 08-31 and 09-03), with the reason attached: "All of those are
 * necessary for the GDS rate loading instructions." The RLI we send to GDS
 * Support has to name which RAC the negotiated rate loads against, and the
 * person writing it was reading it off a spreadsheet.
 *
 * IT NEEDED NO SCHEMA WORK, which is the part worth recording. The ask was
 * logged as blocked on "which system holds these", and the answer turned out to
 * be this one: `hsp.customer_rac(customer_id, rac, gds)` has existed since the
 * BTP loop was designed, with `/api/btp/racs` already serving it. Two of the six
 * fields she listed were a UI gap, not a data gap. The other four are genuinely
 * absent and are recorded in docs/hsp-working-session-2026-09-03.md.
 *
 * GROUPED BY GDS, because the GDS is the half that decides where the RAC is
 * used: an RLI targets one system, so "Sabre: 0FR, SM8" is the shape of the
 * question. Flat, a customer with codes in three systems reads as one list of
 * unrelated tokens.
 *
 * SELF-FETCHING, matching SupplierContactLog rather than AgreementRollup. The
 * customers list endpoint does not carry racs and adding them to it would put a
 * per-row join on a paged table to serve a panel that is only sometimes opened.
 * The list is small and bounded per customer.
 */
import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface CustomerRac {
  id: string;
  rac: string;
  gds: string;
}

export interface CustomerRacsProps {
  customerId: string;
  testId?: string;
}

export function CustomerRacs({
  customerId,
  testId = 'customer-racs',
}: CustomerRacsProps) {
  const [racs, setRacs] = useState<CustomerRac[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<{ racs: CustomerRac[] }>(
      `/api/btp/racs?customer_id=${encodeURIComponent(customerId)}`,
    )
      .then((d) => {
        if (!cancelled) setRacs(d.racs ?? []);
      })
      .catch((e) => {
        if (!cancelled) {
          // An ERROR, not an empty list. "This customer has no access codes" is
          // a claim about the customer that would send someone to set one up;
          // a failed request is a claim about us. The rollup was corrected for
          // exactly this conflation on 2026-08-09.
          setError(e instanceof ApiError ? e.message : String(e));
          setRacs(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  if (loading && racs === null) {
    return (
      <p className="detail-note" data-testid={`${testId}-loading`}>
        <span className="muted">Loading access codes…</span>
      </p>
    );
  }

  if (error) {
    return (
      <p className="detail-note" data-testid={`${testId}-error`}>
        <span className="muted">
          Access codes aren&rsquo;t available right now — this is a loading or
          connection problem, not a customer without codes.
        </span>
      </p>
    );
  }

  const rows = racs ?? [];

  if (rows.length === 0) {
    return (
      <p className="detail-note" data-testid={`${testId}-empty`}>
        <span className="muted">
          No rate access codes recorded for this customer. A rate cannot be
          loaded without one, so this blocks the loading instruction rather than
          being a gap in the record.
        </span>
      </p>
    );
  }

  /*
   * Grouped in insertion order, which the API already sorts by GDS then RAC —
   * so this preserves a decision made server-side rather than re-sorting to a
   * second opinion about the order.
   */
  const byGds = new Map<string, string[]>();
  for (const r of rows) {
    const list = byGds.get(r.gds) ?? [];
    list.push(r.rac);
    byGds.set(r.gds, list);
  }

  return (
    <dl className="detail-grid" data-testid={testId}>
      {Array.from(byGds, ([gds, codes]) => (
        <div className="detail-field" key={gds}>
          <dt>{gds}</dt>
          <dd data-testid={`${testId}-${gds}`}>
            <span className="mono">{codes.join(', ')}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}
